import { alpha } from '@mui/material/styles'

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'

// SETUP COLORS
const PRIMARY = {
  lighter: '#D5E0F4',
  light: '#81A3DD',
  main: '#1D3A6C',
  dark: '#132748',
  darker: '#0A1324',
}
const PRIMARY_DARK = {
  lighter: '#c3d3ef',
  light: '#b5c8eb',
  main: '#A3BBE6',
  dark: '#7282a1',
  darker: '#4f5b70',
}
const SECONDARY = {
  lighter: '#FFF8E0',
  light: '#FFDF7C',
  main: '#FFC905',
  dark: '#FFB100',
  darker: '#FF8C00',
}
const INFO = {
  lighter: '#DDF3FD',
  light: '#7DD3F6',
  main: '#1EB2F0',
  dark: '#0B74A0',
  darker: '#042F40',
}
const SUCCESS = {
  lighter: '#E8F5E9',
  light: '#81C784',
  main: '#388E3C',
  dark: '#2E7D32',
  darker: '#1B5E20',
}
const WARNING = {
  lighter: '#FFF3E0',
  light: '#FFB74D',
  main: '#F57C00',
  dark: '#E65100',
  darker: '#BF360C',
}
const ERROR = {
  lighter: '#FFEBEE',
  light: '#E57373',
  main: '#D32F2F',
  dark: '#C62828',
  darker: '#B71C1C',
}

const GREY = {
  100: '#f5f5f3',
  200: '#f1f1ef',
  300: '#dfdfdd',
  400: '#cacac8',
  500: '#9e9d9b',
  600: '#747472',
  700: '#525251',
  800: '#2e2e2d',
  900: '#20201f',
}

/**
 * Translucent greys behind divider and action colours. Local on purpose: the Minimals template
 * published these on theme.palette.grey as 500_8, 500_24 and so on, which is not a shape MUI's
 * Color type has. Call alpha(theme.palette.grey[500], n) if you need one in a component.
 */
const GREY_ALPHA = {
  8: alpha(GREY[500], 0.08),
  16: alpha(GREY[500], 0.16),
  24: alpha(GREY[500], 0.24),
  80: alpha(GREY[500], 0.8),
}

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: GREY[800] },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  divider: GREY_ALPHA[24],
  action: {
    hover: GREY_ALPHA[8],
    selected: GREY_ALPHA[16],
    disabled: GREY_ALPHA[80],
    disabledBackground: GREY_ALPHA[24],
    focus: GREY_ALPHA[24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
}

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[800], secondary: GREY[700], disabled: GREY[500] },
    background: { paper: '#fff', default: GREY[200], neutral: GREY[100], dark: '#161B24' },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    primary: PRIMARY_DARK,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: '#212936', default: '#191D2A', neutral: '#1d242f', dark: '#161B24' },
    action: { active: GREY[500], ...COMMON.action },
  },
} as const

export default palette
